import { useState, useEffect } from 'react';
import { supabase, signInWithGoogle } from '../supabase/config';

/**
 * Custom hook for Supabase authentication
 * @returns {Object} Authentication state and methods
 */
export function useSupabaseAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initial session check and auth state subscription
  useEffect(() => {
    // Check for existing session
    const checkUser = async () => {
      try {
        setLoading(true);
        
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        
        // Set the user if session exists
        setUser(session?.user || null);
      } catch (err) {
        console.error('Error checking auth status:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    // Call the function
    checkUser();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user || null);
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe();
  }, []);

  /**
   * Sign up with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Result of operation
   */
  const signUp = async (email, password, options = {}) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: options.metadata // Optional user metadata
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error signing up:', err);
      setError(err);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Result of operation
   */
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error signing in:', err);
      setError(err);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign in with Google OAuth
   * @returns {Promise<Object>} Result of operation
   */
  const signInWithGoogleOAuth = async () => {
    try {
      setLoading(true);
      return await signInWithGoogle();
    } catch (err) {
      console.error('Error with Google sign-in:', err);
      setError(err);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign out the current user
   * @returns {Promise<Object>} Result of operation
   */
  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('Error signing out:', err);
      setError(err);
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  // Return the user, loading state, error state, and auth methods
  return {
    user,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    signInWithGoogleOAuth
  };
}

export default useSupabaseAuth;
